/* eslint-disable @typescript-eslint/no-deprecated -- DEPRECATION: [APPDEV-17834] BREAKING-CHANGE: migrate to ChartTooltip before next major. */
import {
  type MouseEvent as ReactMouseEvent,
  type PropsWithChildren,
} from 'react';

import { ChartActionsMenu } from '../../../../core/components/series-action/ChartActionsMenu.js';
import { useChartActionsMenu } from '../../../../core/components/series-action/hooks/useChartActionsMenu.js';
import { getDefaultAction } from '../../../../core/components/series-action/hooks/useDefaultAction.js';
import { InteractiveChartTooltip } from '../../../../core/components/tooltip/InteractiveChartTooltip/components/InteractiveChartTooltip.js';
import { SeriesBasedContent } from '../../../../core/components/tooltip/InteractiveChartTooltip/contents/SeriesBasedContent.js';
import { TimebasedTooltipHeader } from '../../../../core/components/tooltip/InteractiveChartTooltip/headers/TimebasedTooltipHeader.js';
import { useSeriesBasedItemEstimatedSize } from '../../../../core/components/tooltip/InteractiveChartTooltip/hooks/useSeriesBasedItemEstimatedSize.js';
import { useTooltipSeriesActions } from '../../../../core/components/tooltip/shared/hooks/useTooltipSeriesActions.js';
import { useScrollResponsiveChartBounds } from '../../../../core/hooks/useScrollResponsiveChartBounds.js';
import { TooltipItem } from '../../../../core/types/tooltip.js';
import { calculateAdjustedTooltipPosition } from '../../../../core/utils/calculate-adjusted-tooltip-position.js';
import { safeGet } from '../../../../core/utils/safe-get.js';
import { useExploreState } from '../../../hooks/store/selectors.js';
import { useTimeseriesFilteredData } from '../../../hooks/useTimeseriesFilteredData.js';
import { useTimeseriesOverlayRef } from '../../../hooks/useTimeseriesOverlayRef.js';
import { useTimeseriesUnits } from '../../../hooks/useTimeseriesUnits.js';
import { useTimeseriesYFormatter } from '../../../hooks/useTimeseriesYFormatter.js';
import { useTooltipConfig } from '../../../hooks/useTooltipConfig.js';
import { useTruncationMode } from '../../../hooks/useTruncationMode.js';
import { TimeseriesSlotProps } from '../../../types/timeseries.js';
import { TimeseriesChartTooltipProps } from '../../types/timeseries-chart-tooltip.js';
import { ADJUSTED_X_AXIS, SHARED_TOOLTIP_VARIANT } from '../constants.js';
import { useSeriesActionClickHandler } from '../hooks/useSeriesActionClickHandler.js';
import { sortTooltipItems } from '../utils/tooltip/sort-tooltip-items.js';
import {
  filterGroupedPoints,
  toTooltipItems,
} from '../utils/tooltip/timeseries-tooltip.js';

/**
 * This component is the outer wrapper of the Tooltip, and it's in charge of
 * changing the position of the tooltip without re-rendering the contents inside
 * if the contents don't change.
 */
const TooltipPositionWrapper = ({
  tooltipItems,
  onMouseLeave,
  onScrollContent,
  children,
}: PropsWithChildren<{
  tooltipItems: TooltipItem[];
  onMouseLeave: (event?: ReactMouseEvent) => void;
  onScrollContent: (event?: Event) => void;
}>) => {
  const { pinned, visible, position } = useExploreState();
  const chartRef = useTimeseriesOverlayRef();
  const bounds = useScrollResponsiveChartBounds(chartRef, pinned)();

  const { seriesDisplayMode } = useTooltipConfig();
  const estimatedItemSize = useSeriesBasedItemEstimatedSize(
    tooltipItems,
    seriesDisplayMode,
  );

  const adjustedTooltipPosition = calculateAdjustedTooltipPosition(
    bounds,
    position,
    { x: ADJUSTED_X_AXIS, y: 0 },
  );

  const showTooltip = tooltipItems.length && visible && adjustedTooltipPosition;

  return showTooltip ? (
    <InteractiveChartTooltip
      position={adjustedTooltipPosition}
      pinned={pinned}
      itemSize={estimatedItemSize}
      updatePositionWhenPinned={true}
      onMouseLeave={onMouseLeave}
      onScrollContent={onScrollContent}
    >
      {children}
    </InteractiveChartTooltip>
  ) : null;
};

/**
 * Tooltip specific to the TimeseriesChart.
 */
export const TimeseriesChartTooltip = ({
  series,
  closestPoint,
  xPoints,
  groupedPoints,
}: TimeseriesChartTooltipProps) => {
  const { seriesDisplayMode, variant } = useTooltipConfig();
  const truncationMode = useTruncationMode();
  const { seriesMap, datapointsMap } = useTimeseriesFilteredData();

  const {
    onLeave: handleMenuLeave,
    onScroll: handleMenuScroll,
    selectedItem,
    isScrolling,
    updateSelectedItem,
    actionsMenuRef,
  } = useChartActionsMenu();

  const formatters = useTimeseriesYFormatter();
  const units = useTimeseriesUnits();

  const getTooltipTimeframe = (): [Date, Date?] => [
    closestPoint.datapoint.start,
    closestPoint.datapoint.end,
  ];

  const pointsToDisplay =
    variant === SHARED_TOOLTIP_VARIANT
      ? sortTooltipItems(xPoints)
      : sortTooltipItems(
          filterGroupedPoints(closestPoint, xPoints, groupedPoints),
        );

  const tooltipItems = toTooltipItems(
    pointsToDisplay,
    formatters,
    datapointsMap,
    units,
  );

  const closestItem = tooltipItems.find((item) => item.id === closestPoint.id);

  const actionClickHandler = useSeriesActionClickHandler(
    tooltipItems.length > 1,
  );

  const actions = useTooltipSeriesActions<TimeseriesSlotProps>(
    series,
    (series) => safeGet(seriesMap, series).id,
    tooltipItems.find((t) => t.id === selectedItem)?.datapoint,
  );

  const isShared = variant === SHARED_TOOLTIP_VARIANT;

  return (
    <TooltipPositionWrapper
      tooltipItems={tooltipItems}
      onMouseLeave={handleMenuLeave}
      onScrollContent={handleMenuScroll}
    >
      <InteractiveChartTooltip.Header>
        <TimebasedTooltipHeader
          timeframe={getTooltipTimeframe()}
          item={isShared && xPoints.length > 1 ? closestItem : undefined}
          seriesDisplayMode={seriesDisplayMode}
        />
      </InteractiveChartTooltip.Header>
      <InteractiveChartTooltip.Body>
        {tooltipItems.map((item, index) => () => {
          const isOpen = selectedItem === item.id;
          return (
            <ChartActionsMenu
              key={`${item.id}`}
              isOpen={isOpen}
              actions={isOpen ? actions(item) : undefined}
              defaultActions={getDefaultAction(item)}
              ref={actionsMenuRef}
              onActionClick={actionClickHandler}
            >
              <SeriesBasedContent
                item={{ ...item, values: item.asyncValues() }}
                onMouseHover={() => updateSelectedItem(item.id)}
                seriesDisplayMode={seriesDisplayMode}
                showDivider={index !== tooltipItems.length - 1}
                truncationMode={truncationMode}
                isScrolling={isScrolling}
              />
            </ChartActionsMenu>
          );
        })}
      </InteractiveChartTooltip.Body>
    </TooltipPositionWrapper>
  );
};
